import { Controller } from "@hotwired/stimulus"

const INSPECTOR_GROUPS = [ "content", "style", "advanced" ]

export default class extends Controller {
  static targets = [
    "schemaInput",
    "fieldCards",
    "fieldCardTemplate",
    "fieldRowTemplate",
    "optionRowTemplate",
    "builderPanel",
    "jsonPanel",
    "jsonEditor",
    "builderTab",
    "jsonTab",
    "jsonError",
    "drawer",
    "drawerTitle",
    "drawerBreadcrumb",
    "drawerRows",
    "drawerAddButton",
    "drawerSaveButton",
    "drawerError",
  ]

  connect() {
    this._mode = "builder"
    this._drawerMode = null
    this._activeContainerPath = null
    this._activeFieldPath = null
    this._drawerDirty = false
    this._drawerSnapshot = null
    this._drawerOptionState = []
    this._drawerParentContext = null

    this.schemaState = this._normalizeSchemaHash(this._parseHiddenSchema())
    this._renderTopLevelCards()
    this._syncSchemaInput()
    this._highlightActiveTab()
  }

  switchToBuilder() {
    if (this._mode === "json") {
      if (!this._applyJsonEditorToState()) return
    }

    this._hideJsonError()
    this.builderPanelTarget.classList.remove("hidden")
    this.jsonPanelTarget.classList.add("hidden")
    this._mode = "builder"
    this._highlightActiveTab()
  }

  switchToJson() {
    this._syncSchemaInput()
    this.jsonEditorTarget.value = JSON.stringify(this.schemaState, null, 2)
    this._hideJsonError()
    this.builderPanelTarget.classList.add("hidden")
    this.jsonPanelTarget.classList.remove("hidden")
    this._mode = "json"
    this._highlightActiveTab()
  }

  submit(event) {
    if (this._mode === "json" && !this._applyJsonEditorToState()) {
      event.preventDefault()
      return
    }

    this._syncSchemaInput()
  }

  addTopLevelField() {
    const fieldName = this._nextFieldName(this.schemaState, "field")
    this.schemaState[fieldName] = this._defaultFieldConfig(this._nextPosition(this.schemaState))
    this._reindexPositions(this.schemaState)
    this._renderTopLevelCards()
    this._syncSchemaInput()
  }

  addDrawerItem() {
    if (this._drawerMode === "nested_fields") {
      this._addDrawerField()
      return
    }

    if (this._drawerMode === "select_options") {
      this._addOptionRow()
    }
  }

  onFieldInputChange(event) {
    const row = event.currentTarget.closest("[data-field-path]")
    const fieldPath = this._readFieldPath(row)
    const fieldConfig = this._fieldAtPath(fieldPath)
    if (!fieldConfig) return

    const prop = event.currentTarget.dataset.fieldProp

    if (prop === "required") {
      fieldConfig.required = event.currentTarget.checked
    } else if (prop === "inspector_group") {
      fieldConfig.inspector_group = this._normalizeInspectorGroup(event.currentTarget.value)
    } else if (prop !== "name") {
      const value = event.currentTarget.value.trim()
      if (value.length === 0) {
        delete fieldConfig[prop]
      } else {
        fieldConfig[prop] = value
      }
    }

    this._markDrawerDirty(row)
    this._syncSchemaInput()
  }

  onFieldNameBlur(event) {
    const row = event.currentTarget.closest("[data-field-path]")
    const fieldPath = this._readFieldPath(row)
    const oldName = fieldPath[fieldPath.length - 1]
    const parentPath = fieldPath.slice(0, -1)
    const parentContainer = this._resolveContainer(parentPath)
    if (!parentContainer) return

    const nextName = event.currentTarget.value.trim()
    if (nextName.length === 0) {
      event.currentTarget.value = oldName
      return
    }
    if (nextName === oldName) return
    if (Object.prototype.hasOwnProperty.call(parentContainer, nextName)) {
      this._showJsonError(`Field name "${nextName}" already exists at this level.`)
      event.currentTarget.value = oldName
      return
    }

    parentContainer[nextName] = parentContainer[oldName]
    delete parentContainer[oldName]
    this._reindexPositions(parentContainer)

    if (this._isDrawerRow(row)) {
      this._drawerDirty = true
      this._renderDrawerRows()
    } else {
      this._renderTopLevelCards()
    }
    this._syncSchemaInput()
  }

  onFieldTypeChange(event) {
    const row = event.currentTarget.closest("[data-field-path]")
    const fieldPath = this._readFieldPath(row)
    const fieldConfig = this._fieldAtPath(fieldPath)
    if (!fieldConfig) return

    const type = event.currentTarget.value.toLowerCase()
    fieldConfig.type = type

    delete fieldConfig.options
    delete fieldConfig.schema
    delete fieldConfig.item_schema

    if (type === "select") fieldConfig.options = []
    if (type === "object") fieldConfig.schema = {}
    if (type === "array") fieldConfig.item_schema = {}

    if (this._drawerMode === "select_options" && this._pathsEqual(this._activeFieldPath, fieldPath) && type !== "select") {
      this._closeDrawer()
    }

    if (this._isDrawerRow(row)) {
      this._drawerDirty = true
      this._renderDrawerRows()
    } else {
      this._renderTopLevelCards()
    }
    this._syncSchemaInput()
  }

  openNestedDrawer(event) {
    const row = event.currentTarget.closest("[data-field-path]")
    const fieldPath = this._readFieldPath(row)
    const fieldConfig = this._fieldAtPath(fieldPath)
    if (!fieldConfig) return

    const nestedKey = fieldConfig.type === "array" ? "item_schema" : "schema"
    if (fieldConfig.type !== "array" && fieldConfig.type !== "object") return

    fieldConfig[nestedKey] = this._normalizeSchemaHash(fieldConfig[nestedKey] || {})

    if (!this._beginDrawerSession("nested_fields")) return
    this._activeContainerPath = [ ...fieldPath, nestedKey ]
    this._renderDrawer()
    this._syncSchemaInput()
  }

  openOptionsDrawer(event) {
    const row = event.currentTarget.closest("[data-field-path]")
    const fieldPath = this._readFieldPath(row)
    const fieldConfig = this._fieldAtPath(fieldPath)
    if (!fieldConfig || fieldConfig.type !== "select") return

    if (this._drawerMode === "nested_fields" && !this.drawerTarget.classList.contains("hidden")) {
      this._drawerParentContext = {
        mode: "nested_fields",
        activeContainerPath: this._activeContainerPath ? [ ...this._activeContainerPath ] : null,
        dirty: this._drawerDirty,
      }
      this._drawerMode = "select_options"
      this._activeContainerPath = null
      this._activeFieldPath = fieldPath
      this._drawerDirty = false
      this._hideDrawerError()
    } else {
      if (!this._beginDrawerSession("select_options")) return
      this._activeFieldPath = fieldPath
    }

    this._drawerOptionState = (Array.isArray(fieldConfig.options) ? fieldConfig.options : []).map((option) => {
      const value = String(option?.value || "").trim()
      const label = String(option?.label || value).trim() || value
      return {
        value,
        label,
        labelTouched: label !== value,
        persisted: true,
        wasTouched: true,
      }
    })

    this._renderDrawer()
  }

  onOptionValueInput(event) {
    const option = this._optionAtEvent(event)
    if (!option) return

    const value = event.currentTarget.value
    option.value = value
    option.wasTouched = true

    if (!option.labelTouched) {
      option.label = value
      const row = event.currentTarget.closest("[data-option-index]")
      const labelInput = row?.querySelector('[data-option-prop="label"]')
      if (labelInput) labelInput.value = value
    }

    this._drawerDirty = true
    this._hideDrawerError()
  }

  onOptionLabelInput(event) {
    const option = this._optionAtEvent(event)
    if (!option) return

    option.label = event.currentTarget.value
    option.labelTouched = true
    option.wasTouched = true
    this._drawerDirty = true
    this._hideDrawerError()
  }

  saveDrawer() {
    if (this._drawerMode === "select_options") {
      const result = this._normalizeOptionRowsForSave()
      if (result.error) {
        this._showDrawerError(result.error)
        return
      }

      const fieldConfig = this._fieldAtPath(this._activeFieldPath)
      if (fieldConfig && fieldConfig.type === "select") {
        fieldConfig.options = result.options
      }

      if (this._drawerParentContext) {
        this._restoreParentDrawer(true)
        this._renderDrawer()
        this._syncSchemaInput()
        return
      }
    }

    this._drawerDirty = false
    this._drawerSnapshot = null
    this._closeDrawer()
    this._renderTopLevelCards()
    this._syncSchemaInput()
  }

  cancelDrawer() {
    if (this._drawerDirty && !window.confirm("Discard drawer edits?")) return

    if (this._drawerMode === "select_options" && this._drawerParentContext) {
      this._restoreParentDrawer(false)
      this._renderDrawer()
      return
    }

    if (this._drawerDirty && this._drawerSnapshot && this._drawerMode === "nested_fields") {
      this.schemaState = this._drawerSnapshot
    }

    this._drawerDirty = false
    this._drawerSnapshot = null
    this._closeDrawer()
    this._renderTopLevelCards()
    this._syncSchemaInput()
  }

  removeField(event) {
    const row = event.currentTarget.closest("[data-field-path]")
    const fieldPath = this._readFieldPath(row)
    const parentPath = fieldPath.slice(0, -1)
    const fieldName = fieldPath[fieldPath.length - 1]
    const container = this._resolveContainer(parentPath)
    if (!container) return

    delete container[fieldName]
    this._reindexPositions(container)

    if (this._isDrawerRow(row)) {
      this._drawerDirty = true
      this._renderDrawerRows()
    } else {
      this._renderTopLevelCards()
    }
    this._syncSchemaInput()
  }

  moveFieldUp(event) {
    this._moveField(event, -1)
  }

  moveFieldDown(event) {
    this._moveField(event, 1)
  }

  removeOption(event) {
    const index = this._optionIndexFromEvent(event)
    if (index < 0) return

    this._drawerOptionState.splice(index, 1)
    this._drawerDirty = true
    this._hideDrawerError()
    this._renderDrawerRows()
  }

  moveOptionUp(event) {
    this._moveOption(event, -1)
  }

  moveOptionDown(event) {
    this._moveOption(event, 1)
  }

  _moveField(event, direction) {
    const row = event.currentTarget.closest("[data-field-path]")
    const fieldPath = this._readFieldPath(row)
    const parentPath = fieldPath.slice(0, -1)
    const fieldName = fieldPath[fieldPath.length - 1]
    const container = this._resolveContainer(parentPath)
    if (!container) return

    const entries = this._orderedEntries(container)
    const index = entries.findIndex(([name]) => name === fieldName)
    const swapIndex = index + direction
    if (index < 0 || swapIndex < 0 || swapIndex >= entries.length) return

    const swapped = entries.slice()
    ;[swapped[index], swapped[swapIndex]] = [swapped[swapIndex], swapped[index]]

    const reordered = {}
    swapped.forEach(([name, config], positionIndex) => {
      reordered[name] = { ...config, position: (positionIndex + 1) * 10 }
    })

    this._replaceContainer(parentPath, reordered)

    if (this._isDrawerRow(row)) {
      this._drawerDirty = true
      this._renderDrawerRows()
    } else {
      this._renderTopLevelCards()
    }
    this._syncSchemaInput()
  }

  _renderTopLevelCards() {
    this.fieldCardsTarget.innerHTML = ""
    this._orderedEntries(this.schemaState).forEach(([fieldName, fieldConfig]) => {
      const card = this.fieldCardTemplateTarget.content.firstElementChild.cloneNode(true)
      this._hydrateFieldRow(card, [ fieldName ], fieldName, fieldConfig)
      this.fieldCardsTarget.appendChild(card)
    })
  }

  _renderDrawer() {
    this.drawerTarget.classList.remove("hidden")
    this._renderDrawerChrome()
    this._renderDrawerBreadcrumb()
    this._renderDrawerRows()
  }

  _renderDrawerRows() {
    const container = this._resolveContainer(this._activeContainerPath)
    this.drawerRowsTarget.innerHTML = ""

    if (this._drawerMode === "nested_fields") {
      if (!this._activeContainerPath || !container) return

      this._orderedEntries(container).forEach(([fieldName, fieldConfig]) => {
        const row = this.fieldRowTemplateTarget.content.firstElementChild.cloneNode(true)
        this._hydrateFieldRow(row, [ ...this._activeContainerPath, fieldName ], fieldName, fieldConfig)
        this.drawerRowsTarget.appendChild(row)
      })
      return
    }

    if (this._drawerMode === "select_options") {
      this._drawerOptionState.forEach((option, index) => {
        const row = this.optionRowTemplateTarget.content.firstElementChild.cloneNode(true)
        this._hydrateOptionRow(row, index, option)
        this.drawerRowsTarget.appendChild(row)
      })
    }
  }

  _hydrateFieldRow(rowElement, fieldPath, fieldName, fieldConfig) {
    rowElement.dataset.fieldPath = JSON.stringify(fieldPath)

    const nameInput = rowElement.querySelector('[data-field-prop="name"]')
    const typeSelect = rowElement.querySelector('[data-field-prop="type"]')
    const labelInput = rowElement.querySelector('[data-field-prop="label"]')
    const helpInput = rowElement.querySelector('[data-field-prop="help"]')
    const requiredInput = rowElement.querySelector('[data-field-prop="required"]')
    const inspectorGroupSelect = rowElement.querySelector('[data-field-prop="inspector_group"]')
    const optionsSection = rowElement.querySelector('[data-field-section="select-options"]')
    const nestedSection = rowElement.querySelector('[data-field-section="nested-actions"]')

    if (nameInput) nameInput.value = fieldName
    if (typeSelect) typeSelect.value = fieldConfig.type || "text"
    if (labelInput) labelInput.value = fieldConfig.label || ""
    if (helpInput) helpInput.value = fieldConfig.help || ""
    if (requiredInput) requiredInput.checked = !!fieldConfig.required
    if (inspectorGroupSelect) {
      inspectorGroupSelect.value = this._normalizeInspectorGroup(fieldConfig.inspector_group)
    }

    if (optionsSection) {
      optionsSection.classList.toggle("hidden", fieldConfig.type !== "select")
    }
    if (nestedSection) {
      const supportsNested = fieldConfig.type === "array" || fieldConfig.type === "object"
      nestedSection.classList.toggle("hidden", !supportsNested)
    }
  }

  _hydrateOptionRow(rowElement, index, option) {
    rowElement.dataset.optionIndex = String(index)

    const valueInput = rowElement.querySelector('[data-option-prop="value"]')
    const labelInput = rowElement.querySelector('[data-option-prop="label"]')

    if (valueInput) valueInput.value = option.value || ""
    if (labelInput) labelInput.value = option.label || ""
  }

  _renderDrawerChrome() {
    if (this._drawerMode === "select_options") {
      this.drawerTitleTarget.textContent = "Select Options"
      this.drawerAddButtonTarget.textContent = "+ Add Option"
      this.drawerSaveButtonTarget.textContent = "Save options"
      return
    }

    this.drawerTitleTarget.textContent = "Nested Fields"
    this.drawerAddButtonTarget.textContent = "+ Add Nested Field"
    this.drawerSaveButtonTarget.textContent = "Save nested edits"
  }

  _renderDrawerBreadcrumb() {
    const labels = [ "Schema" ]
    const path = this._drawerMode === "select_options" ? this._activeFieldPath : this._activeContainerPath
    if (!path) return

    path.forEach((segment) => {
      if (segment === "schema") return
      if (segment === "item_schema") {
        labels.push("Item")
      } else {
        labels.push(segment)
      }
    })

    if (this._drawerMode === "select_options") labels.push("Options")
    this.drawerBreadcrumbTarget.textContent = labels.join(" > ")
  }

  _applyJsonEditorToState() {
    try {
      const parsed = JSON.parse(this.jsonEditorTarget.value)
      if (parsed === null || Array.isArray(parsed) || typeof parsed !== "object") {
        this._showJsonError("Schema JSON must be an object.")
        return false
      }

      this.schemaState = this._normalizeSchemaHash(parsed)
      this._renderTopLevelCards()
      this._hideJsonError()
      this._syncSchemaInput()
      return true
    } catch (error) {
      this._showJsonError(`Invalid JSON: ${error.message}`)
      return false
    }
  }

  _parseHiddenSchema() {
    try {
      const parsed = JSON.parse(this.schemaInputTarget.value || "{}")
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed
    } catch (_) {}
    return {}
  }

  _normalizeSchemaHash(rawSchema) {
    if (!rawSchema || typeof rawSchema !== "object" || Array.isArray(rawSchema)) return {}

    const normalized = {}
    let position = 10

    this._orderedEntries(rawSchema).forEach(([fieldName, rawConfig]) => {
      const safeName = String(fieldName || "").trim()
      if (!safeName) return

      const config = rawConfig && typeof rawConfig === "object" ? { ...rawConfig } : {}
      const type = String(config.type || "text").trim().toLowerCase()

      const field = {
        type,
        position: Number.isInteger(Number(config.position)) ? Number(config.position) : position,
        required: !!config.required,
        inspector_group: this._normalizeInspectorGroup(config.inspector_group),
      }

      ;[ "label", "help", "placeholder", "icon", "description", "accept", "default", "condition" ].forEach((key) => {
        const value = String(config[key] || "").trim()
        if (value.length > 0) field[key] = value
      })

      ;[ "min_items", "max_items", "max_size", "min", "max" ].forEach((key) => {
        const numeric = Number(config[key])
        if (Number.isFinite(numeric)) field[key] = numeric
      })

      if (Array.isArray(config.crop_ratios)) {
        field.crop_ratios = config.crop_ratios.map((ratio) => String(ratio).trim()).filter(Boolean)
      }

      if (type === "select") {
        field.options = (Array.isArray(config.options) ? config.options : []).map((option) => {
          if (option && typeof option === "object") {
            const value = String(option.value || "").trim()
            if (!value) return null
            const label = String(option.label || value).trim() || value
            return { value, label }
          }

          const value = String(option || "").trim()
          if (!value) return null
          return { value, label: value }
        }).filter(Boolean)
      }

      if (type === "object") {
        field.schema = this._normalizeSchemaHash(config.schema || {})
      }

      if (type === "array") {
        field.item_schema = this._normalizeSchemaHash(config.item_schema || {})
      }

      normalized[safeName] = field
      position += 10
    })

    this._reindexPositions(normalized)
    return normalized
  }

  _orderedEntries(schemaHash) {
    if (!schemaHash || typeof schemaHash !== "object") return []
    return Object.entries(schemaHash).sort(([, a], [, b]) => {
      const aPos = Number(a?.position || 0)
      const bPos = Number(b?.position || 0)
      if (aPos !== bPos) return aPos - bPos
      return 0
    })
  }

  _reindexPositions(container) {
    this._orderedEntries(container).forEach(([fieldName, fieldConfig], index) => {
      container[fieldName] = { ...fieldConfig, position: (index + 1) * 10 }
    })
  }

  _replaceContainer(path, nextValue) {
    if (!path.length) {
      this.schemaState = nextValue
      return
    }

    const parent = this._resolveContainer(path.slice(0, -1))
    if (!parent) return
    parent[path[path.length - 1]] = nextValue
  }

  _fieldAtPath(path) {
    const parent = this._resolveContainer(path.slice(0, -1))
    if (!parent) return null
    return parent[path[path.length - 1]]
  }

  _resolveContainer(path) {
    if (!path || !path.length) return this.schemaState

    let cursor = this.schemaState
    for (const segment of path) {
      if (!cursor || typeof cursor !== "object") return null
      cursor = cursor[segment]
    }

    return cursor
  }

  _nextFieldName(container, baseName) {
    let index = 1
    let candidate = baseName
    while (Object.prototype.hasOwnProperty.call(container, candidate)) {
      index += 1
      candidate = `${baseName}_${index}`
    }
    return candidate
  }

  _nextPosition(container) {
    return this._orderedEntries(container).reduce((max, [, fieldConfig]) => {
      const position = Number(fieldConfig?.position || 0)
      return Math.max(max, Number.isFinite(position) ? position : 0)
    }, 0) + 10
  }

  _defaultFieldConfig(position = 10) {
    return { type: "text", required: false, inspector_group: "content", position }
  }

  _normalizeInspectorGroup(value) {
    const candidate = String(value || "").trim().toLowerCase()
    return INSPECTOR_GROUPS.includes(candidate) ? candidate : "content"
  }

  _readFieldPath(row) {
    try {
      return JSON.parse(row.dataset.fieldPath || "[]")
    } catch (_) {
      return []
    }
  }

  _isDrawerRow(row) {
    return this.drawerRowsTarget.contains(row)
  }

  _markDrawerDirty(row) {
    if (this._isDrawerRow(row)) this._drawerDirty = true
  }

  _beginDrawerSession(mode) {
    if (!this._prepareDrawerTransition()) return false

    this._drawerMode = mode
    this._activeContainerPath = null
    this._activeFieldPath = null
    this._drawerOptionState = []
    this._hideDrawerError()
    return true
  }

  _prepareDrawerTransition() {
    if (!this.drawerTarget.classList.contains("hidden") && this._drawerDirty) {
      if (!window.confirm("Discard drawer edits?")) return false
      if (this._drawerMode === "nested_fields" && this._drawerSnapshot) {
        this.schemaState = this._deepClone(this._drawerSnapshot)
      }
    }

    this._drawerSnapshot = this._deepClone(this.schemaState)
    this._drawerDirty = false
    this._hideDrawerError()
    return true
  }

  _closeDrawer() {
    this._drawerMode = null
    this._activeContainerPath = null
    this._activeFieldPath = null
    this._drawerOptionState = []
    this._drawerParentContext = null
    this.drawerTarget.classList.add("hidden")
    this._hideDrawerError()
  }

  _addDrawerField() {
    if (!this._activeContainerPath) return

    const container = this._resolveContainer(this._activeContainerPath)
    if (!container) return

    const fieldName = this._nextFieldName(container, "nested_field")
    container[fieldName] = this._defaultFieldConfig(this._nextPosition(container))
    this._reindexPositions(container)
    this._drawerDirty = true
    this._hideDrawerError()
    this._renderDrawerRows()
    this._syncSchemaInput()
  }

  _addOptionRow() {
    this._drawerOptionState.push({
      value: "",
      label: "",
      labelTouched: false,
      persisted: false,
      wasTouched: false,
    })
    this._drawerDirty = true
    this._hideDrawerError()
    this._renderDrawerRows()
  }

  _moveOption(event, direction) {
    const index = this._optionIndexFromEvent(event)
    const swapIndex = index + direction
    if (index < 0 || swapIndex < 0 || swapIndex >= this._drawerOptionState.length) return

    ;[this._drawerOptionState[index], this._drawerOptionState[swapIndex]] = [this._drawerOptionState[swapIndex], this._drawerOptionState[index]]
    this._drawerDirty = true
    this._hideDrawerError()
    this._renderDrawerRows()
  }

  _optionIndexFromEvent(event) {
    const row = event.currentTarget.closest("[data-option-index]")
    const index = Number(row?.dataset.optionIndex)
    return Number.isInteger(index) ? index : -1
  }

  _optionAtEvent(event) {
    const index = this._optionIndexFromEvent(event)
    if (index < 0) return null
    return this._drawerOptionState[index] || null
  }

  _normalizeOptionRowsForSave() {
    const seenValues = new Set()
    const normalized = []

    for (const option of this._drawerOptionState) {
      const value = String(option.value || "").trim()
      let label = String(option.label || "").trim()
      const untouchedBlank = !option.persisted && !option.wasTouched && !value && !label

      if (untouchedBlank) continue
      if (!value) return { error: "Option values cannot be blank." }

      if (!label) label = value
      if (seenValues.has(value)) return { error: `Option value "${value}" is duplicated.` }

      seenValues.add(value)
      normalized.push({ value, label })
    }

    return { options: normalized }
  }

  _restoreParentDrawer(markDirty) {
    if (!this._drawerParentContext) return

    this._drawerMode = this._drawerParentContext.mode
    this._activeContainerPath = this._drawerParentContext.activeContainerPath
    this._activeFieldPath = null
    this._drawerOptionState = []
    this._drawerDirty = markDirty || this._drawerParentContext.dirty
    this._drawerParentContext = null
    this._hideDrawerError()
  }

  _pathsEqual(left, right) {
    return JSON.stringify(left || []) === JSON.stringify(right || [])
  }

  _syncSchemaInput() {
    this.schemaInputTarget.value = JSON.stringify(this.schemaState)
  }

  _highlightActiveTab() {
    const isBuilder = this._mode === "builder"
    this.builderTabTarget.classList.toggle("btn-primary-sm", isBuilder)
    this.builderTabTarget.classList.toggle("btn-secondary-sm", !isBuilder)
    this.jsonTabTarget.classList.toggle("btn-primary-sm", !isBuilder)
    this.jsonTabTarget.classList.toggle("btn-secondary-sm", isBuilder)
  }

  _showJsonError(message) {
    this.jsonErrorTarget.textContent = message
    this.jsonErrorTarget.classList.remove("hidden")
  }

  _hideJsonError() {
    this.jsonErrorTarget.textContent = ""
    this.jsonErrorTarget.classList.add("hidden")
  }

  _showDrawerError(message) {
    this.drawerErrorTarget.textContent = message
    this.drawerErrorTarget.classList.remove("hidden")
  }

  _hideDrawerError() {
    if (!this.hasDrawerErrorTarget) return
    this.drawerErrorTarget.textContent = ""
    this.drawerErrorTarget.classList.add("hidden")
  }

  _deepClone(value) {
    return JSON.parse(JSON.stringify(value))
  }
}
